/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import {
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	Skeleton,
	Stack,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useCreateContactMutation, useListContactsQuery } from '@/features/contacts/contactsApi';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import ClientSchemaProvider, { useClientSchemaContext } from '@/context/clientSchemaContext';
import { isProspectFieldVisible, VIEW_TYPES } from '@/features/config/helper';
import { useParams } from 'react-router-dom';
import { useGetFormConfigQuery } from '@/features/config/configApi';
import DevJSONView from '@/components/DevJSONView';
import { ContactField } from '@/components/forms/CreateClientForm/Fields/ContactFields';

const GROUP = 'contact';

const AddContactFormWrapper = ({ onClose }) => {
	const params = useParams();
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const [createContact, createContactState] = useCreateContactMutation();

	const {
		isProspect,
		view,
		initialValues,
		schema: validationSchema,
		isLoading,
	} = useClientSchemaContext();

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const fieldsQuery = useGetFormConfigQuery(
		{ configType: 'client' },
		{
			skip: !userDetails,
			selectFromResult: ({ data, ...rest }) => ({
				...rest,
				data:
					data?.filter(
						(f) =>
							f.group === GROUP &&
							(isProspect ? isProspectFieldVisible(f, view) : f.visible?.[view]),
					) ?? [],
			}),
		},
	);

	const fields = useMemo(() => {
		const data = (fieldsQuery.data ?? []).slice().map((f) => ({
			...f,
			required: f.mandatory?.[isProspect ? 'prospect' : 'client'],
		}));
		return data.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fieldsQuery.data, isProspect]);

	const { data: contacts, ...contactQuery } = useListContactsQuery(params.clientId, {
		skip: !params.clientId,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});

	useEffect(() => console.log('Client contacts', contacts), [contacts]);
	const hasExistingContact = contacts.length > 0;

	return (
		<Formik
			{...{
				initialValues,
				validationSchema,
				validateOnBlur: true,
				enableReinitialize: true,
				onSubmit: async (values, { resetForm }) => {
					console.log('[onSubmit] Submitting values', values);
					const { contacts } = values;
					const newContact = contacts[0];

					try {
						await createContact({
							clientId: params.clientId,
							...newContact,
						}).unwrap();

						dispatch(
							showToast({
								message: 'Created new contact!',
								success: true,
								autohide: true,
							}),
						);
						resetForm();
						onClose?.();
					} catch (e) {
						console.error('[onSubmit] Error adding contact', e);
						const message = e.data?.message ?? e?.message;
						dispatch(
							showToast({
								message: `Error adding contact: ${message}`,
								error: true,
							}),
						);
					}
				},
			}}
		>
			{({ isSubmitting, values, handleSubmit, errors, validateForm, resetForm }) => {
				const disabled = isSubmitting || createContactState.isLoading;

				return (
					<Form>
						<FormikErrorScroll {...{ isSubmitting }} />
						<DialogTitle>Add Contact</DialogTitle>
						<DialogContent sx={{ paddingBottom: '2em' }}>
							{isLoading || fieldsQuery.isLoading || fieldsQuery.isUninitialized ? (
								<Skeleton width='100%' height='3em' />
							) : (
								<Stack spacing={1}>
									<ContactField
										{...{
											contact: values.contacts[0],
											index: 0,
											view: VIEW_TYPES.create,
											fields,
											// expanded,
											// setExpanded: handleAccordionChange,
											disabled,
										}}
									/>
								</Stack>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={() => {
									resetForm();
									onClose?.();
								}}
								color='primary'
							>
								Cancel
							</Button>

							<Button
								color='primary'
								variant='contained'
								disabled={isSubmitting}
								onClick={async () => {
									await handleSubmit();
									setTimeout(() => {
										validateForm();
									}, 100);
								}}
							>
								{isSubmitting ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : hasExistingContact ? (
									'Add'
								) : (
									'Create'
								)}
							</Button>
						</DialogActions>
						<DevJSONView value={{ values, errors }} />
					</Form>
				);
			}}
		</Formik>
	);
};

export const AddContactDialog = () => {
	const dispatch = useDispatch();

	const open = useSelector((state) => state.dialog.open.contact);

	const handleClose = () => {
		dispatch(closeDialog('contact'));
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			sx={{ minWidth: '100%', padding: '1em', maxWidth: 'md' }}
			fullWidth
		>
			{open && (
				<ClientSchemaProvider {...{ view: VIEW_TYPES.update, variant: 'addContact' }}>
					<AddContactFormWrapper onClose={handleClose} />
				</ClientSchemaProvider>
			)}
		</Dialog>
	);
};
